import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MainLayout from "../../components/Layouts/Main/MainLayout.index";
import {
  getAllADListForUser,
  getADDailyImpressions, // Add the matching API export in ../../api/project
} from "../../api/project";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DAYS = 14;
const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];
const weekday = (date) => WEEKDAYS[new Date(`${date}T12:00:00`).getDay()];
const dateLabel = (date) =>
  `${date.slice(5).replace("-", "/")}(${weekday(date)})`;
const HOME_ITEMS = [
  { ad_id: "JM_home_ini", ad_name: "雀活マスター 起動", isHome: true },
  { ad_id: "OSHICAL_home_ini", ad_name: "OSHICAL 起動", isHome: true },
];
const num = (value) => Number(value || 0).toLocaleString("ja-JP");
const toDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function getDateRange() {
  // Exclude today: hourly history summary is only complete up to yesterday.
  const yesterday = new Date();
  yesterday.setHours(12, 0, 0, 0);
  yesterday.setDate(yesterday.getDate() - 1);
  const dates = Array.from({ length: DAYS }, (_, i) => {
    const date = new Date(yesterday);
    date.setDate(yesterday.getDate() - (DAYS - 1 - i));
    return toDateString(date);
  });
  return {
    dates,
    startDate: dates[0],
    endDateExclusive: toDateString(
      new Date(
        yesterday.getFullYear(),
        yesterday.getMonth(),
        yesterday.getDate() + 1,
      ),
    ),
  };
}

function Sparkline({ data, label = "表示回数" }) {
  return (
    <div
      style={{ width: 165, height: 46 }}
      aria-label={`直近14日間の${label}推移`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 5, right: 4, bottom: 5, left: 4 }}
        >
          <Line
            type="monotone"
            dataKey="impressions"
            stroke="#2563eb"
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
          <Tooltip
            formatter={(v) => [`${num(v)}回`, label]}
            labelFormatter={(label, payload) =>
              payload?.[0]?.payload?.date || label
            }
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ADList() {
  const [adList, setAdList] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAdId, setSelectedAdId] = useState("ALL");
  const navigate = useNavigate();
  const range = useMemo(() => getDateRange(), []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const [adsRes, dailyRes] = await Promise.all([
          getAllADListForUser({ checkToken: "jankatsu_token" }),
          getADDailyImpressions({
            checkToken: "jankatsu_token",
            startDate: range.startDate,
            endDate: range.endDateExclusive,
          }),
        ]);
        if (!alive) return;
        if (adsRes.code !== 200 || dailyRes.code !== 200)
          throw new Error("API response not OK");
        setAdList(Array.isArray(adsRes.data) ? adsRes.data : []);
        setDailyRows(Array.isArray(dailyRes.data) ? dailyRes.data : []);
      } catch (error) {
        if (alive) toast.error("広告データの取得に失敗しました");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [range]);

  const byAd = useMemo(() => {
    const map = new Map();
    for (const row of dailyRows) {
      const adId = String(row.banner_id ?? "");
      const date = String(row.add_date ?? "")
        .slice(0, 10)
        .replaceAll("/", "-");
      if (!adId || !range.dates.includes(date)) continue;
      if (!map.has(adId)) map.set(adId, new Map());
      const dayMap = map.get(adId);
      dayMap.set(date, (dayMap.get(date) || 0) + Number(row.impressions || 0));
    }
    return map;
  }, [dailyRows, range]);

  const metricsSource = useMemo(() => {
    const ids = new Set(adList.map((ad) => ad.ad_id));
    return [...adList, ...HOME_ITEMS.filter((item) => !ids.has(item.ad_id))];
  }, [adList]);

  const adMetrics = useMemo(
    () =>
      metricsSource.map((ad) => {
        const dayMap = byAd.get(ad.ad_id) || new Map();
        const series = range.dates.map((date) => ({
          date,
          impressions: dayMap.get(date) || 0,
        }));
        const total = series.reduce((sum, day) => sum + day.impressions, 0);
        return { ...ad, series, total, average: Math.round(total / DAYS) };
      }),
    [metricsSource, byAd, range],
  );

  // Keep ordinary impressions separate from app-start events.
  const overviewData = useMemo(
    () =>
      range.dates.map((date) => ({
        date,
        impressions: adMetrics
          .filter((ad) => !ad.isHome)
          .reduce((sum, ad) => sum + (byAd.get(ad.ad_id)?.get(date) || 0), 0),
      })),
    [adMetrics, byAd, range],
  );
  const homeMetrics = adMetrics.filter((ad) => ad.isHome);
  const homeTotal = homeMetrics.reduce((sum, ad) => sum + ad.total, 0);
  const selectedAd = adMetrics.find((ad) => ad.ad_id === selectedAdId);
  const chartData = selectedAd ? selectedAd.series : overviewData;
  const total = chartData.reduce((sum, day) => sum + day.impressions, 0);
  const average = Math.round(total / DAYS);
  const metricLabel = selectedAd?.isHome ? "起動回数" : "表示回数";

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URLをコピーしました");
    } catch {
      toast.error("コピーに失敗しました");
    }
  };
  const goDetail = (adId, adName) => {
    sessionStorage.setItem("ad_banner_id", adId);
    sessionStorage.setItem("ad_banner_name", adName);
    navigate("/dashboard");
  };

  return (
    <MainLayout>
      <h2>広告一覧・表示回数</h2>
      <style>{`
        .ad-mobile-list { display: none; }
        .ad-desktop-table { width: 100%; overflow-x: auto; }
        .ad-desktop-table th, .ad-desktop-table td { padding: 8px; }
        @media (max-width: 767px) {
          .ad-summary { padding: 12px !important; }
          .ad-summary select { width: 100%; }
          .ad-desktop-table { display: none; }
          .ad-mobile-list { display: grid; gap: 12px; min-width: 0; }
          .ad-mobile-card, .ad-mobile-overview { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; min-width: 0; }
          .ad-mobile-card-heading { display: flex; gap: 8px; align-items: flex-start; justify-content: space-between; }
          .ad-mobile-card-heading strong { display: block; overflow-wrap: anywhere; }
          .ad-mobile-card-heading small { display: block; color: #64748b; overflow-wrap: anywhere; }
          .ad-mobile-card button { border: 1px solid #cbd5e1; background: #fff; border-radius: 8px; padding: 6px 10px; flex-shrink: 0; }
          .ad-mobile-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; margin: 12px 0; }
          .ad-mobile-stats span { font-size: 11px; color: #64748b; }
          .ad-mobile-stats b { display: block; font-size: 16px; color: #0f172a; }
          .ad-mobile-spark { margin-bottom: 8px; }
          .ad-daily-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 5px; }
          .ad-day { display: flex; align-items: center; justify-content: space-between; gap: 4px; background: #f8fafc; border-radius: 6px; padding: 6px 7px; font-size: 12px; min-width: 0; }
          .ad-day span { color: #64748b; white-space: nowrap; }
          .ad-day b { white-space: nowrap; font-variant-numeric: tabular-nums; }
          .ad-mobile-url { display: flex; align-items: center; justify-content: space-between; gap: 6px; margin-top: 12px; font-size: 12px; }
          .ad-mobile-url span { overflow-wrap: anywhere; min-width: 0; }
        }
      `}</style>

      <ToastContainer position="top-right" autoClose={2000} />
      {loading ? (
        <div style={{ padding: 20 }}>読み込み中...</div>
      ) : (
        <>
          <section
            className="ad-summary"
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 20,
              marginBottom: 24,
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div>
                <h3 style={{ margin: "0 0 4px" }}>
                  {selectedAd ? selectedAd.ad_name : "全広告合計"}・日別
                  {metricLabel}
                </h3>
                <small>
                  {range.startDate} ～ {range.dates[DAYS - 1]}
                  （確定済み過去14日）
                </small>
              </div>
              <select
                value={selectedAdId}
                onChange={(e) => setSelectedAdId(e.target.value)}
                aria-label="表示対象広告を選択"
                style={{ padding: 8, maxWidth: "100%" }}
              >
                <option value="ALL">全広告合計（起動を除く）</option>
                {adMetrics.map((ad) => (
                  <option key={ad.ad_id} value={ad.ad_id}>
                    {ad.ad_name} ({ad.ad_id})
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                display: "flex",
                gap: 28,
                margin: "16px 0",
                flexWrap: "wrap",
              }}
            >
              <div>
                <small>14日間合計</small>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {num(total)} 回
                </div>
              </div>
              <div>
                <small>1日平均</small>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {num(average)} 回
                </div>
              </div>
              <div>
                <small>直近日</small>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {num(chartData[DAYS - 1]?.impressions)} 回
                </div>
              </div>
            </div>
            <div style={{ width: "100%", height: 300, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 18, left: 0, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    interval="preserveStartEnd"
                    minTickGap={25}
                    tick={{ fontSize: 11 }}
                    tickFormatter={dateLabel}
                  />
                  <YAxis
                    width={60}
                    tickFormatter={(v) => num(v)}
                    allowDecimals={false}
                  />
                  <Tooltip
                    labelFormatter={dateLabel}
                    formatter={(v) => [`${num(v)} 回`, metricLabel]}
                  />
                  <Line
                    type="monotone"
                    dataKey="impressions"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 7 }}
                    isAnimationActive
                    animationDuration={650}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
          <section
            style={{
              marginBottom: 20,
              padding: 16,
              border: "1px solid #e5e7eb",
              borderRadius: 12,
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              アプリ起動回数（広告表示とは別集計）
            </h3>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {homeMetrics.map((ad) => (
                <div key={ad.ad_id} style={{ minWidth: 220 }}>
                  <strong>{ad.ad_name}</strong>
                  <div>昨日：{num(ad.series[DAYS - 1]?.impressions)} 回</div>
                  <div>日平均：{num(ad.average)} 回</div>
                </div>
              ))}
              <div>
                <strong>起動合計</strong>
                <div>14日間：{num(homeTotal)} 回</div>
              </div>
            </div>
          </section>
          <div className="ad-desktop-table">
            <table
              style={{ width: "100%", borderCollapse: "collapse" }}
              border="1"
            >
              <thead>
                <tr>
                  <th>広告名</th>
                  <th>14日間推移</th>
                  <th>昨日</th>
                  <th>日平均（14日）</th>
                  <th>14日間合計</th>
                  <th>広告URL（ユーザー提示用）</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {adMetrics.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center" }}>
                      データがありません
                    </td>
                  </tr>
                ) : (
                  adMetrics.map((ad) => {
                    const adUrl = ad.isHome
                      ? ""
                      : `${process.env.REACT_APP_AD_URL}\n${ad.user_id}/${ad.password}`;
                    return (
                      <tr key={ad.ad_id}>
                        <td>
                          {ad.ad_name}
                          {ad.isHome ? "（起動）" : ""}
                        </td>
                        <td>
                          <Sparkline
                            data={ad.series}
                            label={ad.isHome ? "起動回数" : "表示回数"}
                          />
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {num(ad.series[DAYS - 1]?.impressions)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {num(ad.average)}
                        </td>
                        <td style={{ textAlign: "right" }}>{num(ad.total)}</td>
                        <td>
                          {!ad.isHome && ad.user_id != null ? (
                            <>
                              {ad.user_id}/{ad.password}{" "}
                              <button
                                onClick={() => copyUrl(adUrl)}
                                aria-label={`${ad.ad_name}のURLをコピー`}
                              >
                                コピー
                              </button>
                            </>
                          ) : null}
                        </td>
                        <td>
                          {ad.isHome ? (
                            "—"
                          ) : (
                            <button
                              onClick={() => goDetail(ad.ad_id, ad.ad_name)}
                            >
                              詳細
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
                {adMetrics.length > 0 && (
                  <tr style={{ fontWeight: 700, background: "#f8fafc" }}>
                    <td>全広告合計</td>
                    <td>—</td>
                    <td style={{ textAlign: "right" }}>
                      {num(overviewData[DAYS - 1]?.impressions)}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {num(
                        Math.round(
                          overviewData.reduce((s, d) => s + d.impressions, 0) /
                            DAYS,
                        ),
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {num(overviewData.reduce((s, d) => s + d.impressions, 0))}
                    </td>
                    <td colSpan={2}>
                      起動・クリックを除く（起動は上段と個別行に表示）
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="ad-mobile-list">
            <div className="ad-mobile-overview">
              <strong>全広告合計（起動を除く）</strong>
              <div className="ad-mobile-stats">
                <span>
                  昨日 <b>{num(overviewData[DAYS - 1]?.impressions)}</b>
                </span>
                <span>
                  日平均{" "}
                  <b>
                    {num(
                      Math.round(
                        overviewData.reduce(
                          (sum, d) => sum + d.impressions,
                          0,
                        ) / DAYS,
                      ),
                    )}
                  </b>
                </span>
                <span>
                  14日合計{" "}
                  <b>
                    {num(
                      overviewData.reduce((sum, d) => sum + d.impressions, 0),
                    )}
                  </b>
                </span>
              </div>
              <div className="ad-daily-grid">
                {overviewData.map((day) => (
                  <div key={day.date} className="ad-day">
                    <span>{dateLabel(day.date)}</span>
                    <b>{num(day.impressions)}</b>
                  </div>
                ))}
              </div>
            </div>
            {adMetrics.map((ad) => {
              const adUrl = ad.isHome
                ? ""
                : `${process.env.REACT_APP_AD_URL}\n${ad.user_id}/${ad.password}`;
              return (
                <article className="ad-mobile-card" key={ad.ad_id}>
                  <div className="ad-mobile-card-heading">
                    <div>
                      <strong>
                        {ad.ad_name}
                        {ad.isHome ? "（起動）" : ""}
                      </strong>
                      <small>{ad.ad_id}</small>
                    </div>
                    {!ad.isHome && (
                      <button
                        type="button"
                        onClick={() => goDetail(ad.ad_id, ad.ad_name)}
                      >
                        詳細
                      </button>
                    )}
                  </div>
                  <div className="ad-mobile-stats">
                    <span>
                      昨日 <b>{num(ad.series[DAYS - 1]?.impressions)}</b>
                    </span>
                    <span>
                      日平均 <b>{num(ad.average)}</b>
                    </span>
                    <span>
                      14日合計 <b>{num(ad.total)}</b>
                    </span>
                  </div>
                  <div className="ad-mobile-spark">
                    <Sparkline
                      data={ad.series}
                      label={ad.isHome ? "起動回数" : "表示回数"}
                    />
                  </div>
                  <div className="ad-daily-grid">
                    {ad.series.map((day) => (
                      <div key={day.date} className="ad-day">
                        <span>{dateLabel(day.date)}</span>
                        <b>{num(day.impressions)}</b>
                      </div>
                    ))}
                  </div>
                  {!ad.isHome && ad.user_id != null && (
                    <div className="ad-mobile-url">
                      <span>
                        {ad.user_id}/{ad.password}
                      </span>
                      <button type="button" onClick={() => copyUrl(adUrl)}>
                        URLコピー
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
          <div style={{ height: 70 }} />
        </>
      )}
    </MainLayout>
  );
}

export default ADList;
